import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCategories } from '../../hooks/useCategories';
import { extractMasterDataArray } from '../../lib/masterDataQuery';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Alert, AlertDescription } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { 
  Tag, 
  RefreshCw, 
  Plus, 
  Edit, 
  Trash2,
  AlertCircle,
  Search,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  ChevronDown,
  FileText
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ticketService } from '../../api/ticketService';
import { createOrUpdateTemplate } from '../../api/templateService';
import { getCurrentUser } from '../../api/authService';
import StatusFilterDropdown from '../../components/filters/StatusFilterDropdown';
import AddEditCategoryModal from '../../components/modals/AddEditCategoryModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import { extractErrorMessage } from '../../utils/errorHandler';
import toast from 'react-hot-toast';

const CategoryManagement = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [message, setMessage] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);

  const { data: categoriesRaw = [], isSuccess: categoriesReady, refetch: refetchCategories } = useCategories();
  const initialCategoriesToastRef = useRef(false);

  const mapCategoriesFromRaw = useCallback((rawCategories) => {
    const categoriesData = (rawCategories || []).filter((cat) => {
      const categoryId = cat.categoryId || cat.id;
      const categoryName = cat.categoryName || cat.name || '';
      return (
        categoryId !== null &&
        categoryId !== undefined &&
        categoryName !== 'Select a category...' &&
        categoryName.trim() !== ''
      );
    });

    return categoriesData.map((cat) => ({
      id: cat.categoryId || cat.id,
      categoryId: cat.categoryId,
      name: cat.categoryName || cat.name || 'Unnamed Category',
      categoryName: cat.categoryName,
      description: cat.description || '',
      departmentId: cat.departmentId || null,
      organizationId: cat.organizationId || null,
      status: cat.isActive === true ? 'active' : (cat.isActive === false ? 'inactive' : 'active'),
      isActive: cat.isActive !== undefined ? cat.isActive : true,
      templateType: cat.templateType || null,
      createdAt: cat.createdAt || new Date().toISOString(),
      updatedAt: cat.updatedAt || null,
    }));
  }, []);

  useEffect(() => {
    if (!categoriesReady) {
      return;
    }
    const mappedCategories = mapCategoriesFromRaw(categoriesRaw);
    setAllCategories(mappedCategories);
    setCategories(mappedCategories);
    if (!initialCategoriesToastRef.current) {
      initialCategoriesToastRef.current = true;
      if (mappedCategories.length > 0) {
        toast.success(`Loaded ${mappedCategories.length} category(ies)`);
      } else {
        toast('No categories found', { icon: 'ℹ️' });
      }
    }
  }, [categoriesReady, categoriesRaw, mapCategoriesFromRaw]);

  // Auto-dismiss messages after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const result = await refetchCategories();
      const mappedCategories = mapCategoriesFromRaw(extractMasterDataArray(result.data));

      setAllCategories(mappedCategories);
      setCategories(mappedCategories);

      if (mappedCategories.length > 0) {
        toast.success(`Loaded ${mappedCategories.length} category(ies)`);
      } else {
        toast('No categories found', { icon: 'ℹ️' });
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      const errorMessage = extractErrorMessage(error, 'Failed to load categories. Please try again.');
      setMessage({ type: 'error', text: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Filter categories based on search and status
  useEffect(() => {
    let filteredCategories = [...allCategories];

    if (selectedStatus) {
      filteredCategories = filteredCategories.filter(cat => 
        cat.status === selectedStatus
      );
    }

    if (searchQuery && searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filteredCategories = filteredCategories.filter(cat =>
        cat && (
          cat?.name?.toLowerCase().includes(query) ||
          cat?.description?.toLowerCase().includes(query) ||
          cat?.code?.toLowerCase().includes(query)
        )
      );
    }

    setCategories(filteredCategories);
  }, [selectedStatus, searchQuery, allCategories]);

  const handleCreateCategory = () => {
    setEditingCategory(null);
    setShowAddCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setShowAddCategoryModal(true);
  };

  const handleDeleteCategory = (categoryId) => {
    const category = allCategories.find(cat => cat.id === categoryId);
    setCategoryToDelete(category);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete) return;

    setSaving(true);
    try {
      // Delete from backend API
      await ticketService.deleteTicketCategory(categoryToDelete.categoryId || categoryToDelete.id);
      
      // Reload categories to reflect deletion
      await loadCategories();
      
      setMessage({ type: 'success', text: 'Category deleted successfully' });
      toast.success('Category deleted successfully');
      setShowDeleteConfirm(false);
      setCategoryToDelete(null);
    } catch (error) {
      console.error('Error deleting category:', error);
      const errorMessage = extractErrorMessage(error, 'Failed to delete category. Please try again.');
      setMessage({ type: 'error', text: errorMessage });
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCategory = async (categoryData) => {
    setSaving(true);
    try {
      // Get current user to extract tenantId
      const currentUser = getCurrentUser();
      if (!currentUser || !currentUser.tenantId) {
        const errorMsg = 'Unable to determine tenant. Please login again.';
        setMessage({ type: 'error', text: errorMsg });
        toast.error(errorMsg);
        setSaving(false);
        return;
      }

      // Extract template fields from categoryData
      const templateFields = categoryData.templateFields || [];
      delete categoryData.templateFields; // Remove from category data
      
      // Prepare data in backend format (TicketCategory entity)
      const backendCategoryData = {
        categoryName: categoryData.categoryName?.trim(),
        description: categoryData.description?.trim() || null,
        departmentId: categoryData.departmentId ? parseInt(categoryData.departmentId) : null,
        organizationId: categoryData.organizationId ? parseInt(categoryData.organizationId) : null,
        isActive: categoryData.isActive !== undefined ? categoryData.isActive : true,
        templateType: categoryData.templateType || '1', // Default to "1" if not provided
        // Note: tenantId is set by backend from TenantContext, but we ensure user is authenticated
        tenantId: currentUser.tenantId
      };

      // Remove null values for optional fields
      if (!backendCategoryData.description) delete backendCategoryData.description;
      if (!backendCategoryData.departmentId) delete backendCategoryData.departmentId;
      if (!backendCategoryData.organizationId) delete backendCategoryData.organizationId;
      if (!backendCategoryData.templateType) delete backendCategoryData.templateType;

      let categoryId;
      if (editingCategory) {
        // Update existing category
        categoryId = editingCategory.categoryId || editingCategory.id;
        const response = await ticketService.updateTicketCategory(categoryId, backendCategoryData);
        const updatedCategory = response?.data?.data || response?.data;
        
        // Reload categories to get updated data
        await loadCategories();
        
        setMessage({ type: 'success', text: 'Category updated successfully' });
        toast.success('Category updated successfully');
      } else {
        // Create new category
        const response = await ticketService.createTicketCategory(backendCategoryData);
        const createdCategory = response?.data?.data || response?.data;
        categoryId = createdCategory?.categoryId || createdCategory?.id;
        
        // Reload categories to get the new category with all fields
        await loadCategories();
        
        // setMessage({ type: 'success', text: 'Category created successfully' });
        toast.success('Category created successfully');
      }
      
      // Save template fields if any are provided
      if (templateFields.length > 0 && categoryId) {
        try {
          // Prepare template request
          const templateRequest = {
            categoryId: categoryId,
            templateName: backendCategoryData.categoryName || 'Default Template',
            description: backendCategoryData.description || null,
            fields: templateFields.map((field, index) => ({
              fieldId: field.fieldId,
              fieldName: field.fieldName.trim(),
              fieldCode: field.fieldCode.trim(),
              fieldType: field.fieldType,
              fieldOptions: (field.fieldType === 'select' || field.fieldType === 'checkbox') 
                ? field.fieldOptions?.trim() || '[]' 
                : null,
              isRequired: field.isRequired || false,
              isActive: field.isActive !== false,
              displayOrder: field.displayOrder !== undefined ? field.displayOrder : index
            }))
          };
          
          await createOrUpdateTemplate(templateRequest);
          toast.success('Template fields saved successfully');
        } catch (templateError) {
          console.error('Error saving template fields:', templateError);
          // Don't fail the whole operation if template save fails
          toast.error('Category saved but template fields failed to save. Please try updating the category again.');
        }
      }
      
      setShowAddCategoryModal(false);
      setEditingCategory(null);
    } catch (error) {
      console.error('Error saving category:', error);
      
      // Handle specific error cases
      if (error.response?.status === 401) {
        // 401 will be handled by apiClient (redirects to signin)
        // But we can still show a message before redirect
        const errorMsg = 'Session expired or authentication failed. Redirecting to login...';
        setMessage({ type: 'error', text: errorMsg });
        toast.error(errorMsg);
      } else if (error.response?.status === 403) {
        const errorMsg = 'You do not have permission to perform this action.';
        setMessage({ type: 'error', text: errorMsg });
        toast.error(errorMsg);
      } else {
        const errorMessage = extractErrorMessage(error, 'Failed to save category. Please check your connection and try again.');
        setMessage({ type: 'error', text: errorMessage });
        toast.error(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  // Parse API response to category list (same logic as loadCategories, without setting state)
  const parseCategoriesResponse = (categoriesResponse) => {
    let categoriesData = [];
    if (categoriesResponse?.data?.data && Array.isArray(categoriesResponse.data.data)) {
      categoriesData = categoriesResponse.data.data;
    } else if (categoriesResponse?.data && Array.isArray(categoriesResponse.data)) {
      categoriesData = categoriesResponse.data;
    } else if (Array.isArray(categoriesResponse)) {
      categoriesData = categoriesResponse;
    } else if (categoriesResponse?.data?.content && Array.isArray(categoriesResponse.data.content)) {
      categoriesData = categoriesResponse.data.content;
    }
    categoriesData = categoriesData.filter(cat => {
      const categoryId = cat.categoryId || cat.id;
      const categoryName = cat.categoryName || cat.name || '';
      return categoryId != null && categoryId !== undefined &&
             categoryName !== 'Select a category...' && categoryName.trim() !== '';
    });
    return categoriesData.map((cat) => ({
      id: cat.categoryId || cat.id,
      categoryId: cat.categoryId,
      name: cat.categoryName || cat.name || 'Unnamed Category',
      categoryName: cat.categoryName || cat.name,
      description: cat.description ?? '',
      departmentId: cat.departmentId ?? null,
      status: cat.isActive === true ? 'active' : (cat.isActive === false ? 'inactive' : 'active'),
      createdAt: cat.createdAt || null
    }));
  };

  const escapeCsvCell = (val) => {
    if (val === null || val === undefined) return '';
    const s = String(val).trim();
    if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };

  const formatDateForCsv = (dateVal) => {
    if (dateVal == null || dateVal === '') return '';
    try {
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
    } catch {
      return '';
    }
  };

  const handleExportCsv = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const list = allCategories;
      if (list.length === 0) {
        toast('No categories to export.', { icon: 'ℹ️' });
        return;
      }
      const headers = ['Category Name', 'Status', 'Description', 'Dept ID', 'Created Date'];
      const rows = list.map((cat) => [
        escapeCsvCell(cat.categoryName || cat.name || ''),
        escapeCsvCell(cat.status || ''),
        escapeCsvCell(cat.description ?? ''),
        escapeCsvCell(cat.departmentId != null ? String(cat.departmentId) : '-'),
        escapeCsvCell(formatDateForCsv(cat.createdAt))
      ]);
      const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `categories-export-${new Date().toISOString().slice(0, 10)}.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${list.length} categor${list.length === 1 ? 'y' : 'ies'} to CSV.`);
    } catch (error) {
      console.error('Export failed:', error);
      const errorMessage = extractErrorMessage(error, 'Failed to export categories. Please try again.');
      toast.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const list = allCategories;
      if (list.length === 0) {
        toast('No categories to export.', { icon: 'ℹ️' });
        return;
      }
      const doc = new jsPDF('l', 'mm', 'a4');
      const headers = [['Category Name', 'Status', 'Description', 'Dept ID', 'Created Date']];
      const body = list.map((cat) => [
        (cat.categoryName || cat.name || '').substring(0, 35),
        (cat.status || '').substring(0, 10),
        (cat.description ?? '').substring(0, 40),
        cat.departmentId != null ? String(cat.departmentId) : '-',
        formatDateForCsv(cat.createdAt)
      ]);
      const config = { head: headers, body, startY: 20, styles: { fontSize: 8 }, headStyles: { fillColor: [34, 197, 94] } };
      if (typeof autoTable === 'function') autoTable(doc, config);
      else if (typeof doc.autoTable === 'function') doc.autoTable(config);
      else throw new Error('PDF table plugin not available.');
      doc.save(`categories-export-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(`Exported ${list.length} categor${list.length === 1 ? 'y' : 'ies'} to PDF.`);
    } catch (error) {
      console.error('Export PDF failed:', error);
      toast.error(extractErrorMessage(error, 'Failed to export categories to PDF. Please try again.'));
    } finally {
      setExporting(false);
    }
  };

  const getCategoryColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const categoryStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const safeCategories = Array.isArray(categories) ? categories : [];
  
  // Pagination logic
  const totalPages = Math.ceil(safeCategories.length / pageSize);
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedCategories = safeCategories.slice(startIndex, endIndex);
  
  // Reset to page 0 when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedStatus, searchQuery]);

  return (
    <div className="py-4 sm:py-6 lg:py-8 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Category Management</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Manage ticket categories</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={loadCategories}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => setExportMenuOpen((prev) => !prev)}
              disabled={loading || exporting}
              className="flex items-center gap-2"
            >
              <Download className={`h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Exporting...' : 'Export'}
              <ChevronDown className="h-4 w-4 opacity-70" />
            </Button>
            {exportMenuOpen && !exporting && (
              <>
                <div className="fixed inset-0 z-10" aria-hidden onClick={() => setExportMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 z-20 min-w-[160px] rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                  <button
                    type="button"
                    onClick={handleExportCsv}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
                  >
                    <Download className="h-4 w-4" />
                    Export CSV
                  </button>
                  <button
                    type="button"
                    onClick={handleExportPdf}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
                  >
                    <FileText className="h-4 w-4" />
                    Export PDF
                  </button>
                </div>
              </>
            )}
          </div>
          <Button
            onClick={handleCreateCategory}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Category
          </Button>
        </div>
      </div>

      {message && (
        <Alert className={`relative ${message.type === 'error' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className={`flex-1 ${message.type === 'error' ? 'text-red-800' : 'text-green-800'}`}>
            {message.text}
          </AlertDescription>
          <button
            onClick={() => setMessage(null)}
            className="absolute top-2 right-2 p-1 rounded hover:bg-black/10 transition-colors"
            aria-label="Close message"
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Categories List
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search categories..."
                    className="pl-10 w-64"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <StatusFilterDropdown
                  value={selectedStatus}
                  options={categoryStatusOptions}
                  placeholder="Filter by Status"
                  onChange={setSelectedStatus}
                  icon={Filter}
                />
              </div>
            </div>

            {(!safeCategories || safeCategories.length === 0) ? (
              <div className="text-center py-12">
                <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">No categories found</p>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedStatus || (searchQuery && searchQuery.trim() !== '')
                    ? 'Try adjusting your filters'
                    : 'No categories available'}
                </p>
                {(selectedStatus || (searchQuery && searchQuery.trim() !== '')) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedStatus(null);
                      setSearchQuery('');
                    }}
                    className="mt-4"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Category Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dept ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created Date
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedCategories.map((category, index) => (
                      <tr key={category?.id || `cat-${index}`} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center mr-3">
                              <Tag className="h-4 w-4 text-purple-600" />
                            </div>
                            <span className="text-sm font-medium text-gray-900">
                              {category?.name || category?.categoryName || 'Unnamed Category'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant="outline" className={`${getCategoryColor(category?.status || 'inactive')} text-xs font-medium`}>
                            {category?.status || 'inactive'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-600">
                            {category?.description || 'No description'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">
                            {category?.departmentId ? category.departmentId : '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-600">
                            {category?.createdAt ? new Date(category.createdAt).toLocaleDateString() : '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditCategory(category)}
                              title="Edit Category"
                              className="h-8 px-3 border-amber-200 bg-white text-amber-600 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition-all"
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              <span className="text-xs">Edit</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteCategory(category.id);
                              }}
                              title="Delete Category"
                              className="h-8 px-3 border-red-200 bg-white text-red-600 hover:bg-red-50 hover:border-red-300 hover:text-red-700 transition-all"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              <span className="text-xs">Delete</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {safeCategories.length > pageSize && (
              <div className="mt-6 flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(endIndex, safeCategories.length)}</span> of{' '}
                  <span className="font-medium">{safeCategories.length}</span> categories
                  {totalPages > 0 && (
                    <span className="ml-2">(Page {currentPage + 1} of {totalPages})</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                    disabled={currentPage === 0 || loading}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600 dark:text-gray-400 px-2">
                    {currentPage + 1} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                    disabled={currentPage >= totalPages - 1 || loading}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
            {safeCategories.length > 0 && safeCategories.length <= pageSize && (
              <div className="mt-6 text-sm text-gray-600 dark:text-gray-400 pt-4 border-t">
                Showing <span className="font-medium">{safeCategories.length}</span> of{' '}
                <span className="font-medium">{safeCategories.length}</span> categories
              </div>
            )}
          </CardContent>
        </Card>
      </div>

        <AddEditCategoryModal
          isOpen={showAddCategoryModal}
          onClose={() => {
            setShowAddCategoryModal(false);
            setEditingCategory(null);
          }}
          category={editingCategory}
          onSave={handleSaveCategory}
          saving={saving}
        />

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setCategoryToDelete(null);
        }}
        onConfirm={confirmDeleteCategory}
        title="Delete Category"
        message={
          categoryToDelete 
            ? `Are you sure you want to delete "${categoryToDelete.name}"? This action cannot be undone and all associated data will be permanently removed.`
            : 'Are you sure you want to delete this category?'
        }
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default CategoryManagement;

